import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { BellRing, Loader2, Send } from 'lucide-react';
import { getCsrfToken } from '../../utils/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PUSH_ITEMS = [
  { key: 'payment_received', label: 'Payment received', description: 'The moment a customer pays your invoice' },
  { key: 'bank_reauth', label: 'Bank reconnection needed', description: 'When a bank feed stops and needs your action' },
  { key: 'billing_alerts', label: 'Billing alerts', description: 'Trial ending soon or a subscription charge failed' },
  { key: 'daily_digest', label: 'Morning summary', description: 'Overdue invoices, bills due and items to review. 9am daily' },
  { key: 'bank_feed_complete', label: 'Statement processed', description: 'When an uploaded statement finishes processing' },
  { key: 'tax_deadlines', label: 'Tax deadlines', description: 'Reminders 7 days and 1 day before (only if tax tracking is on)' },
  { key: 'business_pulse', label: 'Monday business pulse', description: 'Your week at a glance, every Monday morning' },
  { key: 'month_end', label: 'Month-end ready', description: 'When your books are closed for the month' },
  { key: 'product_tips', label: 'Product tips', description: 'Occasional tips and new features (off by default)' },
];

export const PushPreferencesCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/push-notifications/preferences`, { credentials: 'include' });
      if (res.ok) setPrefs(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/push-notifications/preferences`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
        body: JSON.stringify(next),
      });
      if (res.ok) toast({ title: 'Push preferences saved' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save push preferences.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/push-notifications/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() || '' },
      });
      const data = await res.json();
      toast({
        title: data.push_sent > 0 ? 'Test push sent' : 'No active device',
        description: data.push_sent > 0 ? 'Check your device.' : 'Enable notifications from the bell icon first.',
      });
    } catch {
      toast({ title: 'Error', description: 'Failed to send test push.', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (!prefs) return null;

  return (
    <Card data-testid="push-preferences-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <BellRing className="h-4 w-4 text-[#283F8B]" />
          <CardTitle className="text-sm font-semibold">Push Notifications</CardTitle>
        </div>
        <CardDescription className="text-xs">
          Delivered only between 8am–8pm in your business's local time.
          {permission !== 'granted' && ' Enable notifications from the bell icon to start receiving them.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-1">
        {PUSH_ITEMS.map((item) => (
          <div key={item.key} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`push-pref-${item.key}`}>
            <div className="flex-1 min-w-0 pr-4">
              <Label className="text-sm font-medium">{item.label}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
            </div>
            <Switch
              data-testid={`push-toggle-${item.key}`}
              checked={prefs?.[item.key] !== false}
              onCheckedChange={(v) => handleToggle(item.key, v)}
            />
          </div>
        ))}

        <div className="flex items-center justify-between py-3">
          <div className="flex-1 min-w-0 pr-4">
            <Label className="text-sm font-medium">Low cash alert threshold</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Alert when total bank balance falls below this amount (leave empty to disable)</p>
          </div>
          <Input
            type="number"
            min="0"
            placeholder="e.g. 50000"
            className="w-32 h-9"
            value={prefs?.low_cash_threshold ?? ''}
            onChange={(e) => handleToggle('low_cash_threshold', e.target.value === '' ? null : Number(e.target.value))}
            data-testid="low-cash-threshold-input"
          />
        </div>

        <div className="flex items-center gap-3 pt-3">
          <Button size="sm" onClick={() => save(prefs)} disabled={saving} className="bg-[#283F8B] hover:bg-[#283F8B]/90" data-testid="save-push-prefs-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Push Preferences
          </Button>
          <Button size="sm" variant="outline" onClick={sendTest} disabled={testing} data-testid="send-test-push-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Test Push
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PushPreferencesCard;
